import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { catchError, firstValueFrom, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

interface Theme {
  logoUrl: string;
  backgroundUrl: string;
  themeColour?: string;
  fontFamily?: string;
}

@Component({
  selector: 'app-b2c-login',
  imports: [FormsModule],
  templateUrl: './b2c-login.component.html',
  styleUrls: ['./b2c-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class B2CLoginComponent implements OnInit {
  readonly loginEmail = signal<string>('');
  readonly loginInProgress = signal<boolean>(false);
  isLoggedIn = signal<boolean>(false);
  errorMessage = signal<string>('');
  invitationError = signal<string>('');
  invitationCode: string = '';
  invitationEmail: string = '';
  private _invitationDomainHint: string | null = null;
  isAccountSetup = signal<boolean>(false);
  accountSetupEmail = signal<string>('');
  private _paramsCleared = false;
  themeLoaded = signal<boolean>(false);
  theme = signal<Theme>({
    logoUrl: '/assets/logo.png', // Fallback logo
    backgroundUrl: '/assets/corporate-bg.png', // Default fallback background
    themeColour: undefined,
    fontFamily: undefined
  });

  // Avoid regex to eliminate any risk of catastrophic backtracking. Simple structural checks only.
  private _isEmailStructurallyValid(value: string): boolean {
    const email: string = value.trim();
    if (email.length === 0) return false;

    const atIndex: number = email.indexOf('@');
    if (atIndex <= 0) return false; // must have something before @
    if (atIndex !== email.lastIndexOf('@')) return false; // only one @

    const local: string = email.slice(0, atIndex);
    const domain: string = email.slice(atIndex + 1);
    if (local.length === 0 || domain.length < 3) return false; // minimal domain like a.b
    if (domain.includes(' ') || local.includes(' ')) return false; // no spaces

    const lastDot: number = domain.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === domain.length - 1) return false; // dot must be inside and not last

    return true;
  }

  private readonly _httpClient = inject(HttpClient);
  // Raw HttpClient without interceptors to avoid MSAL redirect before login
  private readonly _rawHttpClient = new HttpClient(inject(HttpBackend));
  private readonly _msalService = inject(MsalService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _titleService = inject(Title);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this._titleService.setTitle('Portal - Sign In');
    this.loadTheme();
    // The view will be updated based on the isLoggedIn signal, which is checked in the template.
    const accounts = this._msalService.instance.getAllAccounts();
    this.isLoggedIn.set(accounts.length > 0);

    // Extract and validate invitation code from query params
    const snap = this._route.snapshot.queryParamMap;

    // Check if this is the account setup view
    if (snap.get('accountSetup') === 'true') {
      this.isAccountSetup.set(true);
      // Store email for the "Go to Login Page" link
      const emailParam = snap.get('email');
      if (emailParam) {
        this.accountSetupEmail.set(emailParam);
      }
    }

    // If the invitation link already carries a domainHint (resolved server-side when the
    // invite was sent), prioritize it and skip the home realm resolution lookup on login.
    const domainHint = snap.get('domainHint');
    if (domainHint) {
      this._invitationDomainHint = domainHint;
    }

    const invitationCode = snap.get('code');
    const invitationEmail = snap.get('email');

    if (invitationCode && invitationEmail) {
      this.invitationCode = invitationCode;
      this.invitationEmail = invitationEmail;
      this.loginEmail.set(invitationEmail); // Pre-fill email
      // Validation will happen when user clicks Continue button
    } else if (invitationEmail && !invitationCode) {
      // Email provided without invitation code (e.g., from account setup redirect)
      this.loginEmail.set(invitationEmail);
    }

    // Set initial error from snapshot
    if (snap?.keys.length) {
      const err = snap.get('error');
      const authCode = snap.get('authCode');
      const reason = snap.get('reason');
      const message = snap.get('message');
      this.errorMessage.set(this._mapErrorToMessage(err, authCode, reason, message));
      // Clear params from URL so error does not persist after valid login
      if (err && !this._paramsCleared) {
        setTimeout(() => {
          this._router.navigate([], { relativeTo: this._route, queryParams: {}, replaceUrl: true });
          this._paramsCleared = true;
        }, 300);
      }
    }

    // Also handle error persisted in sessionStorage across logout redirects
    try {
      const raw = sessionStorage.getItem('loginError');
      if (raw) {
        const payload = JSON.parse(raw);
        this.errorMessage.set(this._mapErrorToMessage(payload?.error, payload?.code, payload?.reason, payload?.message));
        sessionStorage.removeItem('loginError');
      }
    } catch (storageError) {
      console.error('[B2CLogin] Failed to read login error from sessionStorage:', storageError);
    }

    // Read query params to display auth/init errors
    this._route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((qp) => {
      const err = qp.get('error');
      const code = qp.get('code');
      const reason = qp.get('reason');
      const message = qp.get('message');
      // If there's no error param in this emission (likely after we cleared it),
      // do not override the currently displayed message.
      if (!err) {
        return;
      }

      this.errorMessage.set(this._mapErrorToMessage(err, code, reason, message));
      // Clear params from URL so error does not persist on refresh
      if (!this._paramsCleared) {
        setTimeout(() => {
          this._router.navigate([], { relativeTo: this._route, queryParams: {}, replaceUrl: true });
          this._paramsCleared = true;
        }, 300);
      }
    });
  }

  // Shared email validator used by both sign-in paths and the template
  isValidEmail(value: string | null | undefined): boolean {
    const email = (value || '').trim();
    return this._isEmailStructurallyValid(email);
  }

  private _mapErrorToMessage(err: string | null, code: string | null, reason: string | null, message: string | null = null): string {
    if (!err) return '';

    // If there's a specific error message from the API, display it directly
    if (message && typeof message === 'string' && message.trim()) {
      // Decode the message in case it was URL-encoded
      try {
        return decodeURIComponent(message.trim());
      } catch {
        return message.trim();
      }
    }

    if (err === 'unauthorized') {
      return 'You are not authorized to access this portal. Please contact your administrator.';
    }

    if (err === 'initFailed') {
      if (code === '404') {
        return 'We could not find your account in this environment. Please verify your email or contact support.';
      }
      if (code === '400') {
        return 'Authentication failed due to invalid token information. Please try signing in again.';
      }
      if (reason === 'timeout') {
        return 'Sign-in took longer than expected. Please try again.';
      }
      return 'We could not initialize your session. Please try signing in again.';
    }

    if (err === 'unauthenticated') {
      return 'Please sign in to continue.';
    }

    if (err === 'reauth-required') {
      return 'Your account is being set up. Please sign in again shortly to continue.';
    }

    return 'An authentication error occurred. Please try again.';
  }


  loadTheme(): void {
    const hostname = globalThis.location.hostname;
    const apiUrl = `${environment.apiConfig.uri}/configuration/theme?hostname=${hostname}`;
    // Use raw client to avoid MSAL interceptor; this endpoint should be public
    this._rawHttpClient.get<{ logoUrl?: string; backgroundUrl?: string; themeColour?: string; fontFamily?: string }>(
      apiUrl
    ).pipe(
      catchError(() => {
        return of(this.theme()); // On error, use the fallback theme
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.theme.set({
        logoUrl: response.logoUrl || '/assets/logo.png',
        backgroundUrl: response.backgroundUrl || '/assets/corporate-bg.png',
        themeColour: response.themeColour,
        fontFamily: response.fontFamily
      });

      // Apply branding CSS variables to document body
      if (typeof document !== 'undefined' && document.body) {
        const currentTheme = this.theme();
        if (currentTheme.themeColour) {
          document.body.style.setProperty('--theme-colour', currentTheme.themeColour);
        }
        if (currentTheme.fontFamily) {
          document.body.style.setProperty('--font-family', currentTheme.fontFamily);
        }
      }

      // Mark theme as loaded to show images
      this.themeLoaded.set(true);
    });
  }

  login(): void {
    // Require an email for the email login flow
    const email = (this.loginEmail() || '').trim();
    if (!this.isValidEmail(email)) {
      this.errorMessage.set('Please enter a valid work email address to continue.');
      return;
    }

    // Clear any previous error
    this.errorMessage.set('');

    this.proceedWithLogin(email);
  }

  private async proceedWithLogin(email: string): Promise<void> {
    if (this.loginInProgress()) return;
    this.loginInProgress.set(true);

    const isInvitationSignup = Boolean(this.invitationCode && this.invitationEmail);

    // If invitation code is present, redeem it before proceeding with login
    if (isInvitationSignup) {
      const redeemSuccess = await this.redeemInvitation();
      if (!redeemSuccess) {
        // Redemption failed - block signup and show error
        this.loginInProgress.set(false);
        return;
      }
      // Mark that this is a signup flow so we can redirect to account-setup after completion
      sessionStorage.setItem('isSignupFlow', 'true');
      sessionStorage.setItem('invitationEmail', this.invitationEmail);
    }

    // Invitation links without a domain hint always use the local-account sign-up journey.
    // Normal sign-in still resolves the user's sign-in type when no hint is provided.
    let signInType: string;
    let domainHint: string;
    if (this._invitationDomainHint) {
      signInType = 'federated';
      domainHint = this._invitationDomainHint;
    } else if (isInvitationSignup) {
      signInType = 'emailAddress';
      domainHint = '';
    } else {
      ({ signInType, domainHint } = await this.getSignInType(email));
    }

    const loginRequest: RedirectRequest = {
      scopes: environment.msalConfig.auth.scopes || []
    };

    // Route to the federated identity provider's issuer when BYOID is configured for this user,
    // otherwise fall back to the local account (email/password) sign-in.
    const isFederated = signInType === 'federated' && !!domainHint;
    loginRequest.domainHint = isFederated ? domainHint : 'LocalAccount';

    // Add login hint from the provided email - but only for local accounts. Combining loginHint
    // with a custom-OIDC federated domainHint triggers AADSTS165000 on the return leg (cold
    // session); this is a known Entra External ID limitation with no fix ETA.
    if (!isFederated) {
      loginRequest.loginHint = email;
    }

    // Force signup page for invitation-based flows - but only for local accounts. Federated
    // users have no local B2C account to create; combining prompt=create with a federated
    // domainHint sends B2C a request its signup journey can't resolve, which fails immediately
    // with AADSTS165000 (user context values not supplied) before ever reaching the IdP.
    if (this.invitationCode && this.invitationEmail && !isFederated) {
      loginRequest.prompt = 'create';
    }

    try {
      this._msalService.loginRedirect(loginRequest);
    } catch {
      this.loginInProgress.set(false);
    }
  }

  private async getSignInType(email: string): Promise<{ signInType: string; domainHint: string }> {
    const apiUrl = `${environment.apiConfig.uri}/identity/sign-in-type?email=${encodeURIComponent(email)}`;

    try {
      // Use raw client to avoid MSAL interceptor; this endpoint is called before authentication
      const response = await firstValueFrom(
        this._rawHttpClient.get<{ signInType: string; domainHint: string }>(apiUrl)
      );
      return {
        signInType: response?.signInType || 'emailAddress',
        domainHint: response?.domainHint || ''
      };
    } catch (error: unknown) {
      console.error('[B2CLogin] Failed to look up sign-in type:', error);
      return { signInType: 'emailAddress', domainHint: '' };
    }
  }

  private async redeemInvitation(): Promise<boolean> {
    const apiUrl = `${environment.apiConfig.uri}/invitations/redeem`;

    try {
      const response = await firstValueFrom(
        this._rawHttpClient.post<{ success: boolean; message: string }>(apiUrl, {
          invitationCode: this.invitationCode,
          email: this.invitationEmail
        })
      );

      if (response?.success) {
        console.log('[Invitation] Successfully redeemed invitation before signup');
        return true;
      } else {
        this.invitationError.set(response?.message || 'Failed to redeem invitation. Please contact your administrator to send a new invitation.');
        return false;
      }
    } catch (error: unknown) {
      console.error('[Invitation] Error redeeming invitation:', error);
      const httpError = error as { error?: { message?: string } };
      const errorMessage = httpError?.error?.message || 'Failed to redeem invitation. Please contact your administrator to send a new invitation.';
      this.invitationError.set(errorMessage);
      return false;
    }
  }

  // Keep the old method for backward compatibility
  signInWithEmail(): void {
    this.login();
  }

  onImageError(imageType: string): void {
    console.error(`Failed to load ${imageType} image`);
  }
}
